import { AppTeamUtil } from '../../core/app-team-util';
import { CategoryItem } from '../categories/category-item.model';
import { ImageAsset } from '../images/image-asset-model';
import { Location } from '../location/location.model';
import { MembershipType } from './membership-type-item.model';
import { MyCampApp } from '../common/enumerations';
import { OrganizationEditModel } from './organization-edit.model';
import { OrganizationLink } from '../link/organization-link.model';
import { RegistrationCode } from '../registration-code/regisration-code.model';
import { Theme } from '../theme/theme.model';

import CheckInCheckOutValidationTypes = MyCampApp.CheckInCheckOutValidationTypes;
import OrganizationDataSyncType = MyCampApp.OrganizationDataSyncType;
import SocialMediaAccountType = MyCampApp.SocialMediaAccountType;

export class SocialMediaAccount
{
    Type!: SocialMediaAccountType
    AccountInfo!: string

    constructor(json?: any)
    {
        if (json)
        {
            Object.assign(this, json);
        }
    }
}

export class TimeZone
{
    Id: string | undefined = undefined;
    DisplayName: string | undefined = undefined;

    constructor(json = null)
    {
        if (json)
        {
            Object.assign(this, json);
        }
    }

    get OffsetAndName(): string
    {
        return this.DisplayName!.length > 12 || this.DisplayName === 'UTC' || this.DisplayName === 'GMT' ?  this.DisplayName! : `(${this.DisplayName!}) ${this.Id}`;
    }
}

export class Organization
{
	ActiveYear!: number;
    CategoryItems: CategoryItem[] = [];
    CategoryMappingFileUrl?: string;
    DataSyncType = OrganizationDataSyncType.None;
    DefaultMemberType?: string;
    EmailFrom?: string;
	EnrollmentYear!: number;
    FeatureJson?: string;
    Group?: string;
    Id!: number;
	InEnrollmentPeriod = false;
    IsFaceRecognitionEnabled = false;
    IsMMSEnabled = false;
    IsDocumentCenterEnabled = false;
    IsCheckInCheckOutEnabled = false;
    IsTwoWaySmsEnabled = false;
    CheckInCheckOutValidationTypes = CheckInCheckOutValidationTypes.None;
    Links: OrganizationLink[] = [];
    Locations: Location[] = [];
    Logo?: ImageAsset;
    LongDescription?: string;
    MaxMembers = -1;
    MaxPhotoCount!: number;
    MembershipTypes: MembershipType[] = []
    Name!: string;
    OptedOutForSignupConfirmationEmail = false;
    OutOfAppDomains?: string;
    Photo?: ImageAsset;
    ProhibitLoginEndDate?: Date;
    ProhibitLoginStartDate?: Date;
    RegistrationCodes: RegistrationCode[] = [];
    ShortDescription?: string;
    SmsYearlyLimit = -1;
    SocialMediaAccounts: SocialMediaAccount[] = [];
    TagLine?: string;
    Theme?: Theme;
    TimeForLastScheduledCheckout = 0;
    TimeZoneId?: string;
    WebSiteUrl?: string;
    WelcomeImage?: ImageAsset;

    constructor(json: any = null)
    {
        if (json)
        {
            // Map the json object to 'this' objects properties.
            Object.assign(this, json);

            if (json.Logo) this.Logo = new ImageAsset(json.Logo);
            if (json.Photo) this.Photo = new ImageAsset(json.Photo);
            if (json.WelcomeImage) this.WelcomeImage = new ImageAsset(json.WelcomeImage);
            if (json.CategoryItems) this.CategoryItems = json.CategoryItems.map((item : any) => new CategoryItem(item));
            if (json.MembershipTypes) this.MembershipTypes = json.MembershipTypes.map((item : any) => new MembershipType(item))
                .sort((t1: MembershipType, t2: MembershipType) => t1.Name.localeCompare(t2.Name));
            if (json.Locations) this.Locations = json.Locations.map((loc : Location) => new Location(loc));
            if (json.SocialMediaAccounts) this.SocialMediaAccounts = json.SocialMediaAccounts.map((item : any) => new SocialMediaAccount(item));
            if (json.Links) this.Links = json.Links.map((item : any) => new OrganizationLink(item));
            if (json.RegistrationCodes) this.RegistrationCodes = json.RegistrationCodes.map((item : any) => new RegistrationCode(item));
            if (json.Theme) { this.Theme = new Theme(json.Theme); }
            if (this.Group == null) this.Group = undefined;
            this.ProhibitLoginStartDate = AppTeamUtil.convertApiDateStringToDate(json['ProhibitLoginStartDate']);
            this.ProhibitLoginEndDate = AppTeamUtil.convertApiDateStringToDate(json['ProhibitLoginEndDate']);
        }
    }

    updateFromModel(model: OrganizationEditModel)
    {
        this.Name = model.Name;
        this.WebSiteUrl = model.WebSiteUrl;
        this.EmailFrom = model.EmailFrom;
        this.ShortDescription = model.ShortDescription;
        this.LongDescription = model.LongDescription;
        this.TagLine = model.TagLine;
        this.TimeZoneId = model.TimeZoneId;
        this.SocialMediaAccounts = model.SocialMediaAccounts;
        this.Links = model.Links;
        this.RegistrationCodes = model.RegistrationCodes;
    }

    getCategoryWithMemberTypes(categoryId: number)
    {
        const category = this.CategoryItems!.find(ci => ci.Id === categoryId);
        if (category?.MemberTypeIds.length ?? 0 > 0) return category;
        category!.MemberTypeIds = this.MembershipTypes!.filter(mt => mt.CategoryItems.find(mtCi => mtCi.Id === category!.Id) !== undefined).map(mt => mt.Id);
        return category;
    }

    getCategoriesWithMemberTypes(): CategoryItem[]
    {
        const that = this;
        return this.CategoryItems!.map(category =>
        {
            if (category.MemberTypeIds.length > 0) { return category; }
            category.MemberTypeIds = that.MembershipTypes!.filter(mt => mt.CategoryItems.find(mtCat => mtCat.Id === category.Id) !== undefined).map(mt => mt.Id);
            return category;
        });
    }

    getCategoriesForMembershipType(membershipTypeId?: number): CategoryItem[]
    {
        const membershipType = this.MembershipTypes!.find(mt => mt.Id === membershipTypeId);
        return ((membershipTypeId === undefined || membershipType === undefined) ? this.CategoryItems : membershipType.CategoryItems)
            .sort((cat1, cat2) => cat1.Name.localeCompare(cat2.Name));
    }

    getCategoriesNotForMembershipType(membershipTypeId?: number): CategoryItem[]
    {
        const membershipType = this.MembershipTypes!.find(mt => mt.Id === membershipTypeId);
        return (membershipTypeId === undefined || membershipType === undefined) ? this.CategoryItems :
            this.CategoryItems.filter(ci => membershipType.CategoryItems.every(ci2 => ci2.Id !== ci.Id));
    }

    getCategoryNamesList(categories: CategoryItem[]): string
    {
        if (categories == null) { return ''; }
        let categoryNameList: string[] = [];
        // categories.forEach(ci => console.log(`${ci.Name}, ${ci.Id}, ${ci.CategoryItemType}`));
        const groupedCategories = AppTeamUtil.groupBy(categories, (ci : CategoryItem) => ci.CategoryItemType);
        for (const categoryType of groupedCategories.keys())
        {
            const categoriesInGroup = groupedCategories.get(categoryType);
            if (categoriesInGroup.length === this.CategoryItems!.filter(cat => cat.CategoryItemType === categoryType).length)
            {
                let categoryName = MyCampApp.getCategoryItemTypeString(categoryType);
                categoryName += categoryName.endsWith('s') ? 'es' : 's';
                categoryNameList.push(`All ${categoryName}`);
            }
            else
            {
                categoryNameList = categoryNameList.concat(categoriesInGroup.map((cat : CategoryItem) => cat.Name));
            }
        }

        return categoryNameList.sort((c1, c2) => c1.localeCompare(c2)).join(', ');
    }

    getHtmlForTableCategoryItems(items: CategoryItem[]): string
    {
        const groups = CategoryItem.grouped(items);
        return '<div class="in-table-category-list">' +
            groups.map((group: any) => {
                return `<div class="category-type"><span class="category-type-name">${group.typeName}:</span><span>${group.itemList}</span></div>`;
            }).join('\n') +
            '</div>';
    }

    hasValidationType(type: CheckInCheckOutValidationTypes): boolean
    {
        return (this.CheckInCheckOutValidationTypes & type) !== 0;
    }
}
